<template>
  <div class="tw-flex tw-h-screen tw-overflow-hidden">
    <t-side-nav :navItems="navItems">
      <template v-slot:dropdown="{ isClosed }" v-if="itemList.length">
        <t-nav-dropdown
          data-test-id="navbar-user"
          @selected="itemSelected"
          :loading="loading"
          :isClosed="isClosed"
          :options="itemList"
          :initial-selected="initialItem"
        />
      </template>
    </t-side-nav>
    <div
      class="tw-ml-14 tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto tw-bg-light lg:tw-ml-0"
    >
      <main class="tw-mb-6">
        <div class="container-fluid">
          <t-page-header :user="user" :user-links="userLinks" />
          <router-view />
        </div>
      </main>
      <cl-footer
        class="tw-mt-auto"
        v-if="logo"
        :version="appVersion"
        :logo="logo"
      />
      <div v-else role="status" class="tw-animate-pulse">
        <div
          class="tw-bg-gray-20 tw-h-20 tw-w-full tw-bg-dorian-gray-100"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { version } from "../../../package";
import { ACCOUNT_TYPE, TIER, USER_LINKS, PRODUCTS } from "../../constants";
import { setSupportLinks } from "@/utils/set-layout-support.js";
import TSideNav from "@/components/TSideNav";
import TPageHeader from "@/components/TPageHeader";
import TNavDropdown from "@/components/TNavDropdown";

export default {
  components: {
    TSideNav,
    TPageHeader,
    TNavDropdown,
  },
  data() {
    return {
      breakpointXL: null,
      closed: false,
      initialCustomer: null,
      userAccountList: null,
      loading: true,
      account_type: ACCOUNT_TYPE.USER,
      navItems: this.$getPrimaryNavItems("end_user"),
      appVersion: version,
      itemList: [],
      initialItem: null,
    };
  },
  computed: {
    ...mapGetters("authentication", [
      "authUser",
      "selectedAccount",
      "selectedUser",
    ]),
    ...mapGetters("userInterface", ["interface", "uiLogo"]),
    ...mapGetters("support", ["support", "userLinks"]),

    user() {
      const email = this.authUser.email ?? null;
      const accountname = this.authUser.accountname ?? null;

      return {
        email,
        accountname,
      };
    },
    logo() {
      return this.uiLogo;
    },
  },
  watch: {
    support: {
      handler(support) {
        const res = setSupportLinks(support);
        const resWithTranslatedTitle = res.map((t) => ({
          ...t,
          title: this.$t(t.title),
        }));
        this.updateUserLinks(resWithTranslatedTitle);
      },
      immediate: true,
    },
    itemList() {
      this.setSelectedItem();
    },
  },
  methods: {
    ...mapActions("authentication", ["setUser"]),
    ...mapActions("pageMeta", ["setParentPath"]),
    ...mapActions("support", [
      "fetchSupport",
      "setUserLinks",
      "updateUserLinks",
    ]),
    ...mapActions("emailsHistory", ["clearSearch"]),

    async itemSelected(item) {
      this.setUser(item);
      if (item.allUsers) {
        this.$router.push("/domain/overview", undefined, { shallow: true });
      } else if (this.$route.path !== "/user") {
        this.$router.push("/", undefined, { shallow: true });
      }
    },

    setSelectedItem() {
      this.initialUser = 0;
    },
    setItemList(item) {
      let otherAccounts = [
        {
          name: item,
          short: item.charAt(0),
          id: 0,
        },
      ];
      if (this.selectedAccount.account_type <= ACCOUNT_TYPE.DOMAIN) {
        otherAccounts.push({
          name: "All Users",
          short: "AU",
          allUsers: true,
        });
      } else {
        otherAccounts.push({
          name: "Logout",
          short: "LO",
          logOut: true,
        });
      }

      return [...otherAccounts];
    },
  },
  async created() {
    const userLinksValue = [
      {
        ...USER_LINKS,
        title: this.$t(PRODUCTS.userLinksTitle),
      },
    ];
    this.setUserLinks(userLinksValue);
  },
  beforeMount() {
    let parentPath = "";
    if (this.selectedAccount.account_type === ACCOUNT_TYPE.SYSTEM) {
      parentPath += "/system";
    }
    if (this.selectedAccount.account_type <= ACCOUNT_TYPE.MSP) {
      parentPath += "/msp";
    }
    if (this.selectedAccount.account_type <= ACCOUNT_TYPE.CUSTOMER) {
      parentPath += "/customer";
    }
    if (this.selectedAccount.account_type <= ACCOUNT_TYPE.DOMAIN) {
      parentPath += "/domain";
    }
    this.setParentPath(parentPath);
  },
  async mounted() {
    try {
      await this.fetchSupport({
        type_id: this.selectedUser.id,
        type: TIER.users,
      });
    } catch (_err) {
      // stub
    }

    const item = this.selectedUser.email;
    this.itemList = this.setItemList(item);
    this.loading = false;
  },
  beforeUnmount() {
    this.clearSearch();
  },
};
</script>
